import React, { useState, useEffect, useLayoutEffect, useMemo, useRef, useCallback } from 'react';
import { Monitor, Clock, Play } from 'lucide-react';
import { getCmsSchedules, getContentStatus, cancelCmsSchedule, removeLoopItemFromSlot } from '../api/contentApi';
import { getActiveProjectId } from '../constants/projectConstants';
import { getDeviceDisplayName, getLastSeen } from '../utils/deviceLabel';

import OnAirNow from './timeline/OnAirNow';
import TimelineAxis, { AXIS_HEIGHT, NowIndicator } from './timeline/TimelineAxis';
import TimelineTrack, { TRACK_HEIGHT, TRACK_GAP } from './timeline/TimelineTrack';
import TimeWindowControls from './timeline/TimeWindowControls';
import { resolveSlotLabel, resolveSlotAsset } from './timeline/labelResolver';
import dashboardSocket from '../sockets/dashboardSocket';
import {
  formatDuration,
  formatMinutes,
  isScheduleForDate,
  nowMinutesOfDay,
  timeToMinutes,
} from './timeline/timeUtils';
import TimelineLanes from '../features/timelineEditor/TimelineLanes';
import InspectorBar from '../features/timelineEditor/InspectorBar';
import { parseHHMM } from '../features/timelineEditor/timelineGeometry';
import { updateScheduleIntent, deleteScheduleIntent, listScheduleIntents } from '../api/schedulesApi';
import {
  computeKnownLocations,
  getGroupKey as getGroupKeyFn,
} from '../utils/screenGrouping';
import './timeline/DayRangeTimeline.css';

const UNASSIGNED_LABEL = 'Unassigned';

/**
 * ScreenTimeline — "Today's Timeline" on the Content dashboard.
 *
 * Rewrite of the pre-Phase-8 24h axis. Key changes vs legacy:
 *  - Default zoom is a 2h focus window centred on NOW (slots never collapse
 *    to invisible slivers at this zoom).
 *  - All online + offline devices render as rows (not just scheduled ones).
 *  - Every slot label is resolved through a content map; raw ids never leak
 *    into the UI even for legacy rows with a filename-as-contentId.
 *  - "On Air Now" strip above, campaign rollup sidebar to the right, tick-
 *    labelled NOW line, and sub-8px slots aggregate into "⋯ N" chips.
 *
 * Legacy component preserved alongside at ScreenTimeline.legacy.jsx for one
 * release's rollback safety.
 */

const LEFT_RAIL_W = 180;

const windowForMode = ({ mode, nowMin, jumpCenter }) => {
  switch (mode) {
    case 'business':
      return { startMin: 8 * 60, endMin: 20 * 60 };
    case 'full':
      return { startMin: 0, endMin: 24 * 60 };
    case 'jump': {
      const c = Number.isFinite(jumpCenter) ? jumpCenter : nowMin;
      return {
        startMin: Math.max(0, c - 60),
        endMin: Math.min(24 * 60, c + 60),
      };
    }
    case 'focus':
    default:
      return {
        startMin: Math.max(0, nowMin - 60),
        endMin: Math.min(24 * 60, nowMin + 60),
      };
  }
};

/**
 * Normalise the devices prop — accept either a bare array or { devices: [...] }
 * so it tolerates future prop-reshuffles without crashing.
 */
const toDeviceList = (p) => {
  if (Array.isArray(p)) return p;
  if (Array.isArray(p?.devices)) return p.devices;
  return [];
};

const ScreenTimeline = ({ devices: devicesProp = [], contentItems: contentItemsProp = null }) => {
  const [schedules, setSchedules] = useState([]);
  const [fallbackContentItems, setFallbackContentItems] = useState([]);
  const [isLoadingSchedules, setIsLoadingSchedules] = useState(true);
  const [isLoadingContent, setIsLoadingContent] = useState(contentItemsProp == null);
  const [windowMode, setWindowMode] = useState('focus');
  const [jumpCenter, setJumpCenter] = useState(null);
  // Date of the day whose schedules we're rendering. Defaults to today.
  // Users can jump forward to any day inside an open schedule's start_date→end_date
  // window — which is how a "next month" recurring schedule becomes visible on
  // the dashboard at all. YYYY-MM-DD so a single string compares cleanly against
  // the ISO slice isScheduleForDate produces.
  // LOCAL-date "today" (not toISOString, which is UTC). IST sits +5:30 of
  // UTC, so between 00:00 and 05:30 IST the UTC slice returns yesterday,
  // which previously dropped today's date-bound intents from the Day filter.
  const localTodayISO = () => {
    const d = new Date();
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  };
  const [viewDate, setViewDate] = useState(() => localTodayISO());
  const todayStr = useMemo(() => localTodayISO(), []);
  const isViewingToday = viewDate === todayStr;
  // Timeline scale. Day is the legacy hour-axis; Week/Month widen the
  // window to multiple days and expand each schedule into one virtual slot
  // per active day so the SAME TimelineTrack renderer can draw bars across
  // the wider axis — no parallel component, identical fonts/layout.
  const [viewScale, setViewScale] = useState('day'); // 'day' | 'week' | 'month'
  // Anchor ISO date for Week/Month windows (window start).
  const [rangeAnchor, setRangeAnchor] = useState(() => localTodayISO());
  // Intent selected by a multi-day bar click — drives the floating InspectorBar
  // (separate from the hour-editor's selectedClipId so both paths stay clean).
  const [rangeSelectedIntentId, setRangeSelectedIntentId] = useState(null);
  const [selectedContentId, setSelectedContentId] = useState(null);
  const [tooltip, setTooltip] = useState({ visible: false, slot: null, label: '', x: 0, y: 0 });
  const [nowMs, setNowMs] = useState(() => Date.now());
  const [loopDrawerSlot, setLoopDrawerSlot] = useState(null);
  const [expandedScreenId, setExpandedScreenId] = useState(null);
  // Which screen's "On Air Now" card is currently focused. Independent of
  // expansion — clicking a row always retargets this; expansion toggles on
  // the same click. Defaults below to the first online screen so the banner
  // is never empty while any device is live.
  const [selectedScreenId, setSelectedScreenId] = useState(null);
  // Selected clip (for the floating inspector). Key = intent.id.
  const [selectedClipId, setSelectedClipId] = useState(null);
  // Intents currently loaded for the expanded screen — lifted so the
  // floating InspectorBar can find the selected clip.
  const [expandedIntents, setExpandedIntents] = useState([]);

  const toggleExpanded = useCallback((screenId) => {
    setExpandedScreenId((prev) => {
      const next = prev === screenId ? null : screenId;
      if (next !== prev) setSelectedClipId(null);
      return next;
    });
  }, []);

  // ESC closes any expanded editor.
  useEffect(() => {
    if (!expandedScreenId) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') setExpandedScreenId(null);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [expandedScreenId]);

  const devices = toDeviceList(devicesProp);
  const viewportRef = useRef(null);
  const [viewportWidth, setViewportWidth] = useState(960);

  // ── Load schedules ────────────────────────────────────────────────
  // Day view reads the legacy + intent union via getCmsSchedules (hour-level
  // projection matters there). Week/Month reads raw intents per screen and
  // merges, because the legacy /schedules endpoint dedupes legacy slot docs
  // ahead of intent projections — which gave the collapsed row a shorter
  // date range than the expanded CampaignBar for the same data. Reading
  // intents directly = same source of truth as the expanded lane.
  const loadSchedules = useCallback(async () => {
    setIsLoadingSchedules(true);
    try {
      const isMulti = viewScale !== 'day';
      if (isMulti) {
        const screenIds = toDeviceList(devicesProp)
          .map((d) => d.deviceId || d.tvId)
          .filter(Boolean);
        const perScreen = await Promise.all(
          screenIds.map((sid) =>
            listScheduleIntents(getActiveProjectId(), sid).catch(() => [])
          )
        );
        const flat = perScreen.flat();
        const todayYmd = new Date().toISOString().slice(0, 10);
        const normalized = flat
          .filter((it) => it && (!it.status || it.status === 'active'))
          .filter((it) => {
            const endDate = it.endDate ? String(it.endDate).slice(0, 10) : null;
            return !endDate || endDate >= todayYmd;
          })
          .flatMap((it) => {
            const baseId = it.scheduleId || it.intentId || it.id || it._id;
            const ids = Array.isArray(it.contentIds) && it.contentIds.length > 0
              ? it.contentIds
              : [it.contentId || null];
            return ids.map((cid, idx) => ({
              ...it,
              scheduleId: idx === 0 ? baseId : `${baseId}_c${idx}`,
              id: it.intentId || it.id || it._id,
              contentId: cid,
              startDate: it.startDate || null,
              endDate: it.endDate || null,
              startTime: it.startTime,
              endTime: it.endTime,
              screenId: it.screenId,
              name: it.name,
              status: it.status || 'active',
            }));
          });
        setSchedules(normalized);
      } else {
        const res = await getCmsSchedules({ projectId: getActiveProjectId() });
        const list = Array.isArray(res?.data) ? res.data : Array.isArray(res) ? res : [];
        setSchedules(list.filter((s) => s.status !== 'cancelled'));
      }
    } catch (err) {
      console.error('Timeline schedule load error:', err);
      setSchedules([]);
    } finally {
      setIsLoadingSchedules(false);
    }
  }, [viewScale, devicesProp]);

  const loadContentFallback = useCallback(async () => {
    if (contentItemsProp != null) return;
    setIsLoadingContent(true);
    try {
      const res = await getContentStatus(getActiveProjectId());
      const list = Array.isArray(res?.content) ? res.content : [];
      setFallbackContentItems(list);
    } catch (err) {
      console.error('Timeline content load error:', err);
      setFallbackContentItems([]);
    } finally {
      setIsLoadingContent(false);
    }
  }, [contentItemsProp]);

  useEffect(() => {
    loadSchedules();
    loadContentFallback();
  }, [loadSchedules, loadContentFallback]);

  // Re-pull schedules every 60s so in-flight schedule edits surface without
  // a manual refresh. Same cadence as ContentDashboard's outer poll.
  useEffect(() => {
    const id = setInterval(loadSchedules, 60000);
    return () => clearInterval(id);
  }, [loadSchedules]);

  // 1s tick so the LIVE card countdown updates each second and
  // concurrentSlots re-evaluates at overlap boundaries without waiting
  // on the 60s schedule refetch.
  useEffect(() => {
    const id = setInterval(() => setNowMs(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);

  // Live push: refetch when backend emits an intent change on the default
  // namespace. Falls back to the 60s poll above if the socket is down.
  useEffect(() => {
    dashboardSocket.connect();
    const handler = () => { loadSchedules(); };
    dashboardSocket.onIntentChanged(handler);
    return () => dashboardSocket.offIntentChanged(handler);
  }, [loadSchedules]);

  // Live viewport width — used only for the aggregation pixel threshold
  // (positioning itself is percentage-based, so layout doesn't depend on this
  // being in sync). Measure post-layout with useLayoutEffect, observe any
  // element resize, and also re-measure on window resize as a belt-and-braces
  // against ResizeObserver quirks in StrictMode double-mounts.
  useLayoutEffect(() => {
    const el = viewportRef.current;
    if (!el) return undefined;
    const measure = () => {
      const w = el.getBoundingClientRect().width;
      if (w > 0) {
        setViewportWidth((prev) => (Math.abs(prev - w) < 1 ? prev : w));
      }
    };
    measure();
    // Re-measure on the next frame so we catch layout that settles after the
    // current synchronous paint (campaign rollup flex-basis, etc.).
    const raf = requestAnimationFrame(measure);
    let ro;
    if (typeof ResizeObserver !== 'undefined') {
      ro = new ResizeObserver(measure);
      ro.observe(el);
    }
    window.addEventListener('resize', measure);
    return () => {
      cancelAnimationFrame(raf);
      ro?.disconnect();
      window.removeEventListener('resize', measure);
    };
  }, []);

  // Slots reference content by nanoid (content_id) but /content-status keys its
  // response by name-or-nanoid, so we index every content record under ALL of
  // its possible identifiers: the response's canonical `contentId`, the raw
  // `contentNanoId`, and `name`. Same entry shows up under multiple keys so
  // lookups from either side hit.
  const contentMap = useMemo(() => {
    const items = contentItemsProp != null ? contentItemsProp : fallbackContentItems;
    const m = new Map();
    for (const c of items || []) {
      const entry = {
        title: c.title,
        name: c.name,
        filename: c.filename || c.fileName,
        thumbnailUrl: c.thumbnailUrl || c.thumbnail || null,
        mediaType: c.mediaType || c.mimeType || null,
      };
      const keys = [c?.contentId, c?.contentNanoId, c?.content_id, c?.id, c?.name];
      for (const k of keys) {
        if (k && !m.has(k)) m.set(k, entry);
      }
    }
    return m;
  }, [contentItemsProp, fallbackContentItems]);

  const nowMin = useMemo(() => nowMinutesOfDay(new Date(nowMs)), [nowMs]);

  const windowDays = viewScale === 'month' ? 30 : viewScale === 'week' ? 7 : 1;
  const isMultiDay = windowDays > 1;

  const windowRange = useMemo(() => {
    if (!isMultiDay) return windowForMode({ mode: windowMode, nowMin, jumpCenter });
    // Multi-day window: minutes-since-windowStart, 1440 per day.
    return {
      startMin: 0,
      endMin: windowDays * 1440,
      windowDays,
      windowStartISO: rangeAnchor,
    };
  }, [isMultiDay, windowDays, rangeAnchor, windowMode, nowMin, jumpCenter]);

  // ── Day view: filter to viewDate. Week/Month: expand each schedule into
  //    one virtual slot PER INTENT spanning its startDate→endDate intersection
  //    with the window. This matches the expanded CampaignBar semantics
  //    (one bar per campaign across its run) so the collapsed overview never
  //    disagrees with the editor below. Previous per-day expansion produced
  //    inconsistent paint — threshold-aggregated on some days, missing on
  //    others (daysOfWeek / date-bound edge cases) — and contradicted the
  //    CampaignBar for the SAME data. ──────────────────────────────────────
  const tracks = useMemo(() => {
    const bySchedule = (() => {
      if (!isMultiDay) {
        return schedules.filter((s) => isScheduleForDate(s, viewDate));
      }
      // Per-day time-of-day pulses: for each intent render ONE virtual slot
      // per day in [startDate, endDate] ∩ [anchor, anchor+windowDays), at
      // absolute minutes = dayIdx*1440 + timeOfDayMinutes. Answers the
      // marketing manager's "what's playing at 11 PM this Friday?" without
      // lying about airtime (a 1h/day intent shows as 7 thin pulses, not
      // one 7-day-wide block). IST-safe: we never route through toISOString.
      const anchor = new Date(`${rangeAnchor}T00:00:00`);
      const dayIdxFromAnchor = (iso) => {
        const d = new Date(`${iso}T00:00:00`);
        if (Number.isNaN(d.getTime())) return 0;
        return Math.round((d - anchor) / 86400000);
      };
      const isoAtDayIdx = (i) => {
        const d = new Date(anchor);
        d.setDate(d.getDate() + i);
        const y = d.getFullYear();
        const mo = String(d.getMonth() + 1).padStart(2, '0');
        const da = String(d.getDate()).padStart(2, '0');
        return `${y}-${mo}-${da}`;
      };
      const out = [];
      for (const s of schedules) {
        const startMin = timeToMinutes(s.startTime);
        const endMin = timeToMinutes(s.endTime);
        if (startMin == null || endMin == null || endMin <= startMin) continue;
        const sStartISO = s.startDate ? String(s.startDate).slice(0, 10) : rangeAnchor;
        const sEndISO = s.endDate ? String(s.endDate).slice(0, 10) : null;
        const fromIdx = Math.max(0, dayIdxFromAnchor(sStartISO));
        const toIdx = sEndISO
          ? Math.min(windowDays - 1, dayIdxFromAnchor(sEndISO))
          : windowDays - 1;
        if (toIdx < 0 || fromIdx > windowDays - 1 || toIdx < fromIdx) continue;
        for (let i = fromIdx; i <= toIdx; i++) {
          out.push({
            ...s,
            scheduleId: `${s.scheduleId || s.id || ''}_d${i}`,
            _startMinAbs: i * 1440 + startMin,
            _endMinAbs: i * 1440 + endMin,
            _dayIdx: i,
            _dayISO: isoAtDayIdx(i),
          });
        }
      }
      return out;
    })();

    const byScreen = new Map();
    for (const s of bySchedule) {
      if (!s.screenId) continue;
      if (!byScreen.has(s.screenId)) byScreen.set(s.screenId, []);
      byScreen.get(s.screenId).push(s);
    }

    // Build a row per known device (online or offline) — scheduled-but-
    // unknown-device rows still appear so we don't swallow data.
    const deviceByKey = new Map();
    for (const d of devices) {
      const key = d.deviceId || d.tvId;
      if (!key) continue;
      deviceByKey.set(key, d);
    }

    const knownLocations = computeKnownLocations(devices);

    const seenKeys = new Set();
    const rows = [];

    for (const d of devices) {
      const key = d.deviceId || d.tvId;
      if (!key) continue;
      seenKeys.add(key);
      const slots = byScreen.get(key) || [];
      rows.push(makeTrack({ screenId: key, slots, device: d, nowMin, knownLocations }));
    }

    // Schedules that reference a screen id with no matching device row
    // (e.g. a screen that was unregistered but still has pending slots).
    for (const [screenId, slots] of byScreen.entries()) {
      if (seenKeys.has(screenId)) continue;
      rows.push(makeTrack({ screenId, slots, device: null, nowMin, knownLocations }));
    }

    // Ordering: by location group, then active-on-air, then online, then offline,
    // then alphabetical within. Ungrouped devices sink to a trailing "Unassigned"
    // bucket via the high-codepoint sentinel.
    rows.sort((a, b) => {
      const ga = a.groupKey || '￿';
      const gb = b.groupKey || '￿';
      const gcmp = ga.localeCompare(gb);
      if (gcmp !== 0) return gcmp;
      const rank = (r) => (r.currentSlot ? 0 : (r.isOffline ? 2 : 1));
      const diff = rank(a) - rank(b);
      if (diff !== 0) return diff;
      return (a.deviceName || '').localeCompare(b.deviceName || '');
    });

    return rows;
  }, [schedules, devices, nowMin, viewDate, isMultiDay, windowDays, rangeAnchor]);

  // Map of group label → count of tracks in that group, used by the header
  // count badges. "Unassigned" rolls up every track whose groupKey is null.
  const groupCounts = useMemo(() => {
    const m = new Map();
    for (const t of tracks) {
      const label = t.groupKey || UNASSIGNED_LABEL;
      m.set(label, (m.get(label) ?? 0) + 1);
    }
    return m;
  }, [tracks]);

  // Current NOW-line content label — prefer the first on-air active slot
  const nowLabel = useMemo(() => {
    const airing = tracks.find((t) => t.currentSlot);
    if (!airing) return null;
    return resolveSlotLabel(airing.currentSlot, contentMap).label;
  }, [tracks, contentMap]);

  // Default the LIVE card to the first online screen (or any screen if none
  // online). Re-home if the previously selected screen has disappeared from
  // the tracks list (e.g. deregistered between polls).
  useEffect(() => {
    if (tracks.length === 0) return;
    const stillExists = selectedScreenId && tracks.some((t) => t.screenId === selectedScreenId);
    if (stillExists) return;
    const firstOnline = tracks.find((t) => !t.isOffline);
    setSelectedScreenId((firstOnline || tracks[0]).screenId);
  }, [tracks, selectedScreenId]);

  const selectedTrack = useMemo(
    () => tracks.find((t) => t.screenId === selectedScreenId) || null,
    [tracks, selectedScreenId],
  );

  const handleHoverSlot = useCallback((slot, x, y, label) => {
    setTooltip({ visible: true, slot, label, x, y });
  }, []);
  const handleLeaveSlot = useCallback(() => {
    setTooltip((prev) => ({ ...prev, visible: false }));
  }, []);

  const handleWindowChange = useCallback(({ mode, center }) => {
    setWindowMode(mode);
    if (mode === 'jump' && Number.isFinite(center)) {
      setJumpCenter(center);
    } else if (mode !== 'jump') {
      setJumpCenter(null);
    }
  }, []);

  const handleSelectContent = useCallback((contentId) => {
    setSelectedContentId((prev) => (prev === contentId ? null : contentId));
  }, []);

  const isLoading = isLoadingSchedules || isLoadingContent;

  if (isLoading && schedules.length === 0) {
    return (
      <div style={cardStyle}>
        <div style={loadingStyle}>
          <Clock size={16} style={{ marginRight: '8px', animation: 'spin 1s linear infinite' }} />
          Loading timeline…
        </div>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
      <style>{`
        @keyframes timelineLivePulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.35; }
        }
      `}</style>

      {/* LIVE card — focused on the selected screen; carousels if looping. */}
      <OnAirNow track={selectedTrack} contentMap={contentMap} nowMs={nowMs} />

      {/* Timeline + rollup sidebar */}
      <div style={cardStyle}>
        {/* Header: title + window controls */}
        <div style={{
          padding: '10px 14px',
          borderBottom: '1px solid var(--border-subtle, #ececea)',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          gap: '10px', flexWrap: 'wrap',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
            <Play size={14} color="var(--gold-500, #c8a147)" />
            <span style={{
              fontSize: '12px', fontWeight: 700, color: 'var(--text-strong)',
              letterSpacing: '0.04em', textTransform: 'uppercase',
            }}>
              {viewScale === 'day' && isViewingToday ? "Today's Timeline" : 'Timeline'}
            </span>
            {/* Day / Week / Month — switches the X-axis scale. Day keeps the
                hour ruler; Week/Month widen the window to 7/30 days and the
                same TimelineTrack renders bars via absolute-minute positioning. */}
            <div className="tl-scale" role="tablist" aria-label="Timeline scale">
              {[
                { key: 'day', label: 'Day' },
                { key: 'week', label: 'Week' },
                { key: 'month', label: 'Month' },
              ].map((opt) => (
                <button
                  key={opt.key}
                  type="button"
                  role="tab"
                  aria-selected={viewScale === opt.key}
                  className={`tl-scale__chip${viewScale === opt.key ? ' tl-scale__chip--active' : ''}`}
                  onClick={() => {
                    setViewScale(opt.key);
                    // Persist expansion + selection across mode switches — user
                    // mental model is "I'm looking at this screen's schedule
                    // at different zoom levels", so expansion shouldn't snap
                    // shut and force another click. Same for the selected clip.
                    setRangeSelectedIntentId(null);
                    if (opt.key === 'day') {
                      // Week/Month → Day: anchor on today if in the current
                      // range, else on the range's first day, so the user
                      // doesn't lose context.
                      if (viewScale !== 'day') {
                        const a = new Date(`${rangeAnchor}T00:00:00`);
                        const step = viewScale === 'month' ? 30 : 7;
                        const end = new Date(a);
                        end.setDate(end.getDate() + step - 1);
                        const todayD = new Date(`${todayStr}T00:00:00`);
                        const inRange = todayD >= a && todayD <= end;
                        setViewDate(inRange ? todayStr : rangeAnchor);
                      }
                    } else {
                      // Day → Week/Month: anchor on the day the user was
                      // viewing, not hardcoded to today. Preserves context.
                      setRangeAnchor(viewScale === 'day' ? viewDate : rangeAnchor);
                    }
                  }}
                >
                  {opt.label}
                </button>
              ))}
            </div>
            {/* Range nav — prev/next step by scale size; date picker jumps to
                any day; Today pill restores the anchor to today. */}
            {(() => {
              // Prev / Next step arrows only — the standalone date picker
              // was confusing next to the Day/Week/Month toggles, and the
              // Today pill is redundant with prev/next.
              const step = viewScale === 'month' ? 30 : viewScale === 'week' ? 7 : 1;
              const anchor = viewScale === 'day' ? viewDate : rangeAnchor;
              const shift = (delta) => {
                const d = new Date(`${anchor}T00:00:00`);
                d.setDate(d.getDate() + delta * step);
                const iso = d.toISOString().slice(0, 10);
                if (viewScale === 'day') setViewDate(iso);
                else setRangeAnchor(iso);
              };
              return (
                <div className="tl-nav">
                  <button type="button" className="tl-nav__btn" onClick={() => shift(-1)} aria-label="Previous">‹</button>
                  <button type="button" className="tl-nav__btn" onClick={() => shift(+1)} aria-label="Next">›</button>
                </div>
              );
            })()}
            <span style={{ fontSize: '11px', color: 'var(--text-subtle)' }}>
              {tracks.length} screen{tracks.length !== 1 ? 's' : ''}
              {viewScale === 'day' && (
                <>
                  {' · '}
                  {formatMinutes(windowRange.startMin)} – {windowRange.endMin >= 24 * 60
                    ? '11:59 PM'
                    : formatMinutes(windowRange.endMin)}
                </>
              )}
            </span>
          </div>
          {viewScale === 'day' && (
            <TimeWindowControls
              mode={windowMode}
              window={windowRange}
              nowMin={nowMin}
              onChange={handleWindowChange}
            />
          )}
        </div>

        <div style={{ minHeight: '160px' }}>
          {/* Axis header row — label spacer + measured viewport for axis ticks */}
          <div style={{ display: 'flex', borderBottom: '1px solid var(--border-subtle, #ececea)' }}>
            <div style={{
              width: `${LEFT_RAIL_W}px`,
              flexShrink: 0,
              height: `${AXIS_HEIGHT}px`,
              borderRight: '1px solid var(--border-subtle, #ececea)',
              backgroundColor: 'var(--surface-sunken, #fafaf9)',
            }} />
            <div
              ref={viewportRef}
              style={{ flex: 1, minWidth: 0, position: 'relative', overflow: 'hidden' }}
            >
              <TimelineAxis window={windowRange} />
            </div>
          </div>

          {/* Per-screen rows — each row = [label | track], clickable to expand the editor below. */}
          {tracks.length === 0 ? (
            <div style={{ padding: '28px 14px', textAlign: 'center', color: 'var(--text-muted)', fontSize: '13px' }}>
              No screens connected. Register a TV to see its timeline here.
            </div>
          ) : (
            <div style={{ position: 'relative' }}>
              {tracks.map((track, idx) => {
                const isExpanded = expandedScreenId === track.screenId;
                const currentGroup = track.groupKey || UNASSIGNED_LABEL;
                const prevGroup = idx > 0 ? (tracks[idx - 1].groupKey || UNASSIGNED_LABEL) : null;
                const showHeader = currentGroup !== prevGroup;
                return (
                  <React.Fragment key={track.screenId}>
                    {showHeader && (
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          padding: '10px 14px 6px',
                          backgroundColor: 'var(--surface-sunken, #fafaf9)',
                          borderTop: idx > 0 ? '1px solid var(--border-subtle, #ececea)' : 'none',
                          borderBottom: '1px solid var(--border-subtle, #ececea)',
                          fontSize: '11px',
                          fontWeight: 600,
                          letterSpacing: '0.04em',
                          textTransform: 'uppercase',
                          color: 'var(--text-muted, #78716c)',
                          gap: '8px',
                        }}
                      >
                        <span>{currentGroup}</span>
                        <span style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          minWidth: '20px',
                          height: '18px',
                          padding: '0 6px',
                          borderRadius: '9px',
                          backgroundColor: 'var(--surface-default, #fff)',
                          border: '1px solid var(--border-subtle, #ececea)',
                          fontSize: '10px',
                          fontWeight: 600,
                          color: 'var(--text-muted, #78716c)',
                          letterSpacing: '0',
                        }}>
                          {groupCounts.get(currentGroup) ?? 0}
                        </span>
                      </div>
                    )}
                    <div
                      role="button"
                      tabIndex={0}
                      aria-expanded={isExpanded}
                      aria-label={`${track.deviceName || track.screenId} — ${isExpanded ? 'close' : 'open'} schedule editor`}
                      onClick={() => {
                        setSelectedScreenId(track.screenId);
                        toggleExpanded(track.screenId);
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          setSelectedScreenId(track.screenId);
                          toggleExpanded(track.screenId);
                        }
                      }}
                      style={{
                        display: 'flex',
                        cursor: 'pointer',
                        backgroundColor: isExpanded ? 'var(--surface-sunken, #fafaf9)' : 'transparent',
                        transition: 'background-color 0.12s ease',
                      }}
                      onMouseEnter={(e) => {
                        if (!isExpanded) e.currentTarget.style.backgroundColor = 'var(--surface-hover, #f5f5f4)';
                      }}
                      onMouseLeave={(e) => {
                        if (!isExpanded) e.currentTarget.style.backgroundColor = 'transparent';
                      }}
                    >
                      <div style={{
                        width: `${LEFT_RAIL_W}px`,
                        flexShrink: 0,
                        borderRight: '1px solid var(--border-subtle, #ececea)',
                      }}>
                        <ScreenLabel track={track} isExpanded={isExpanded} />
                      </div>
                      <div style={{ flex: 1, minWidth: 0, position: 'relative', overflow: 'hidden' }}>
                        <TimelineTrack
                          track={track}
                          window={windowRange}
                          viewportWidth={viewportWidth}
                          contentMap={contentMap}
                          nowMin={nowMin}
                          nowMs={nowMs}
                          selectedContentId={selectedContentId}
                          onSelectContent={handleSelectContent}
                          onHoverSlot={handleHoverSlot}
                          onLeaveSlot={handleLeaveSlot}
                          onOpenLoopDrawer={isMultiDay ? undefined : setLoopDrawerSlot}
                          openSlotId={loopDrawerSlot?.scheduleId || loopDrawerSlot?.id || null}
                          onSelectIntent={isMultiDay ? ((slot) => setRangeSelectedIntentId(slot.id || null)) : undefined}
                          selectedIntentId={isMultiDay ? rangeSelectedIntentId : null}
                          suppressAggregation={isMultiDay}
                        />
                      </div>
                    </div>
                    {isExpanded && (
                      <TimelineLanes
                        screenId={track.screenId}
                        projectId={getActiveProjectId()}
                        windowRange={windowRange}
                        viewportWidth={viewportWidth}
                        contentMap={contentMap}
                        selectedClipId={selectedClipId}
                        setSelectedClipId={setSelectedClipId}
                        onIntentsChange={setExpandedIntents}
                        isMultiDay={isMultiDay}
                        windowDays={windowDays}
                        rangeAnchor={rangeAnchor}
                        onDrillToDay={(iso) => {
                          setViewDate(iso);
                          setViewScale('day');
                        }}
                      />
                    )}
                  </React.Fragment>
                );
              })}

              {/* NOW-indicator layer: inset by LEFT_RAIL_W so the
                  percentage-based positioning inside NowIndicator resolves
                  against the viewport column's width, not the full row
                  width. Otherwise the line draws ~180px too far left and
                  stops matching the ruler's tick positions above. */}
              {tracks.length > 0 && (
                <div
                  style={{
                    position: 'absolute',
                    left: `${LEFT_RAIL_W}px`,
                    right: 0,
                    top: 0,
                    bottom: 0,
                    pointerEvents: 'none',
                  }}
                >
                  <NowIndicator
                    window={windowRange}
                    totalHeight={8000 /* spans all rows visually; clipped by outer overflow */}
                    nowMin={nowMin}
                    currentLabel={nowLabel}
                  />
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Floating tooltip */}
      <SlotTooltip {...tooltip} contentMap={contentMap} nowMs={nowMs} />

      {/* Loop drawer — opens on any slot click so operators can cancel it */}
      <LoopDrawer
        slot={loopDrawerSlot}
        contentMap={contentMap}
        nowMs={nowMs}
        onClose={() => setLoopDrawerSlot(null)}
        onCancelSlot={() => { loadSchedules(); }}
      />

      {/* Floating inspector — same component and write path for Day and
          Week/Month. TimelineLanes registers its patch/delete handlers on
          window.__timelineLanesHandlers for the expanded screen, and the
          inspector calls through regardless of the axis scale. */}
      {selectedClipId && expandedScreenId && (() => {
        const selected = expandedIntents.find((it) => it.id === selectedClipId);
        if (!selected) return null;
        const handlers = (typeof window !== 'undefined' && window.__timelineLanesHandlers?.[expandedScreenId]) || {};
        return (
          <InspectorBar
            intent={selected}
            onPatch={(id, partial) => {
              handlers.patchLocal?.(id, partial);
              handlers.scheduleServerPatch?.(id, partial);
            }}
            onDelete={(id) => handlers.delete?.(id)}
            onClose={() => setSelectedClipId(null)}
          />
        );
      })()}
    </div>
  );
};

// ── Helpers ─────────────────────────────────────────────────────────────

const makeTrack = ({ screenId, slots, device, nowMin, knownLocations = [] }) => {
  const deviceName = device ? getDeviceDisplayName(device) : screenId;
  const status = String(device?.status || '').toLowerCase();
  const isOffline = !device || status !== 'online';
  const groupKey = device ? getGroupKeyFn(device, knownLocations) : null;

  const sorted = slots.slice().sort(
    (a, b) => timeToMinutes(a.startTime) - timeToMinutes(b.startTime),
  );

  // concurrentSlots = every slot whose [start,end) brackets nowMin. Multiple
  // entries mean the resolver will loop through them on the TV; OnAirNow
  // uses this to render a carousel instead of a single-content card.
  const concurrentSlots = [];
  let nextSlot = null;
  for (const s of sorted) {
    const sm = timeToMinutes(s.startTime);
    const em = timeToMinutes(s.endTime);
    if (nowMin >= sm && nowMin < em) {
      concurrentSlots.push(s);
    } else if (!nextSlot && sm >= nowMin) {
      nextSlot = s;
    }
  }
  const currentSlot = concurrentSlots[0] || null;

  const lastSeenAt = getLastSeen(device);

  return {
    screenId,
    device,
    deviceName,
    location: device?.location ?? null,
    groupKey,
    slots: sorted,
    isOffline,
    currentSlot,
    concurrentSlots,
    nextSlot,
    lastSeenAt,
  };
};

const ScreenLabel = ({ track }) => {
  const { deviceName, isOffline, lastSeenAt, currentSlot } = track;

  let subline = null;
  if (isOffline) {
    const d = lastSeenAt ? new Date(lastSeenAt) : null;
    if (d && !isNaN(d.getTime())) {
      const hh = d.getHours();
      const mm = String(d.getMinutes()).padStart(2, '0');
      const ampm = hh < 12 ? 'AM' : 'PM';
      const h12 = hh % 12 || 12;
      subline = `offline since ${h12}:${mm} ${ampm}`;
    } else {
      subline = 'offline';
    }
  } else if (currentSlot) {
    const endMin = timeToMinutes(currentSlot.endTime);
    const startMin = timeToMinutes(currentSlot.startTime);
    const nowM = nowMinutesOfDay();
    const remaining = endMin - nowM;
    subline = `on air · ${formatDuration(Math.max(0, remaining))} left`;
    void startMin;
  } else {
    subline = 'online · idle';
  }

  return (
    <div
      style={{
        height: `${TRACK_HEIGHT}px`,
        marginBottom: `${TRACK_GAP}px`,
        padding: '0 12px',
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        overflow: 'hidden',
        opacity: isOffline ? 0.65 : 1,
      }}
    >
      <Monitor
        size={14}
        color={isOffline
          ? 'var(--text-subtle)'
          : (currentSlot ? 'var(--color-success-500, #10b981)' : 'var(--text-muted)')}
      />
      <div style={{ minWidth: 0, flex: 1 }}>
        <div style={{
          fontSize: '11px',
          fontWeight: 700,
          color: 'var(--text-strong)',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}>
          {deviceName || 'Unknown screen'}
        </div>
        <div style={{
          fontSize: '10px',
          color: 'var(--text-muted)',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          marginTop: '1px',
        }}>
          {subline}
        </div>
      </div>
    </div>
  );
};

const SlotTooltip = ({ visible, slot, label, x, y, contentMap, nowMs }) => {
  const tooltipRef = useRef(null);
  const [pos, setPos] = useState({ left: x + 12, top: y - 8 });

  // After the tooltip mounts at the cursor anchor, measure its actual size
  // and flip horizontally / vertically if it would overflow the viewport.
  // Without this, hovering rows near the bottom (or far right) clips the
  // preview off-screen.
  useLayoutEffect(() => {
    if (!visible || !tooltipRef.current) return;
    const rect = tooltipRef.current.getBoundingClientRect();
    const W = window.innerWidth;
    const H = window.innerHeight;
    const PAD = 8;
    let left = x + 12;
    let top = y - 8;
    if (left + rect.width > W - PAD) left = Math.max(PAD, x - rect.width - 12);
    if (top + rect.height > H - PAD) top = Math.max(PAD, y - rect.height - 8);
    setPos({ left, top });
  }, [visible, slot, x, y]);

  if (!visible || !slot) return null;
  const startMin = timeToMinutes(slot.startTime);
  const endMin = timeToMinutes(slot.endTime);
  const durStr = formatDuration(endMin - startMin);
  const hasLoop = slot.loopEnabled && Array.isArray(slot.loopItems) && slot.loopItems.length > 0;

  // Figure out which loop item is currently airing (same math as the track).
  let activeIdx = -1;
  let totalLoopSec = 0;
  if (hasLoop) {
    totalLoopSec = slot.loopItems.reduce((a, x) => a + (x.durationSeconds || 10), 0);
    const now = nowMs || Date.now();
    const isActive = (() => {
      const nowMin = (new Date(now).getHours() * 60) + new Date(now).getMinutes();
      return nowMin >= startMin && nowMin < endMin;
    })();
    if (isActive && totalLoopSec > 0) {
      const todayStart = new Date(now); todayStart.setHours(0, 0, 0, 0);
      const slotStartMs = todayStart.getTime() + startMin * 60 * 1000;
      let cur = Math.max(0, Math.floor((now - slotStartMs) / 1000)) % totalLoopSec;
      for (let i = 0; i < slot.loopItems.length; i += 1) {
        const d = slot.loopItems[i].durationSeconds || 10;
        if (cur < d) { activeIdx = i; break; }
        cur -= d;
      }
    }
  }

  const resolveName = (cid) => {
    if (!cid) return '(unknown)';
    const c = contentMap?.get?.(cid);
    return c?.title || c?.name || c?.filename || String(cid);
  };

  const resolveThumb = (cid) => {
    if (!cid) return null;
    const c = contentMap?.get?.(cid);
    return c?.thumbnailUrl || null;
  };

  const isImageish = (mt) => !mt || String(mt).toLowerCase().startsWith('image');
  const isVideo = (mt) => mt && String(mt).toLowerCase().startsWith('video');

  // For non-loop slots: pick the slot's own asset.
  const singleAsset = !hasLoop ? resolveSlotAsset(slot, contentMap) : null;
  const singleUrl = singleAsset?.thumbnailUrl || null;
  const singleMediaType = singleAsset?.mediaType || null;
  const singleHasUsableImg = singleUrl && isImageish(singleMediaType);
  const singleIsVideo = singleAsset && isVideo(singleMediaType);
  const singleHasPreview = singleHasUsableImg || (singleIsVideo && singleUrl);

  // For loop slots: pre-resolve each loop item's thumb + media-type so the
  // strip can render placeholders for missing/video items without breaking.
  const LOOP_STRIP_MAX = 8;
  const loopThumbs = hasLoop
    ? slot.loopItems.slice(0, LOOP_STRIP_MAX).map((li) => {
        const c = li?.contentId ? contentMap?.get?.(li.contentId) : null;
        const url = c?.thumbnailUrl || null;
        const mt = li?.mediaType || c?.mediaType || c?.mimeType || null;
        return {
          url,
          mediaType: mt,
          usableImg: !!url && isImageish(mt),
          isVideo: isVideo(mt),
        };
      })
    : [];
  const loopOverflow = hasLoop ? Math.max(0, slot.loopItems.length - LOOP_STRIP_MAX) : 0;

  return (
    <div ref={tooltipRef} style={{
      position: 'fixed',
      left: pos.left,
      top: pos.top,
      backgroundColor: 'var(--gray-900, #1c1917)',
      color: '#fff',
      padding: singleHasPreview ? '0' : '10px 12px',
      borderRadius: '10px',
      fontSize: '12px',
      lineHeight: 1.45,
      zIndex: 100,
      pointerEvents: 'none',
      maxWidth: 'min(640px, 92vw)',
      minWidth: hasLoop ? '320px' : (singleHasPreview ? '480px' : 0),
      overflow: 'hidden',
      boxShadow: '0 12px 32px rgba(0,0,0,0.32)',
    }}>
      {/* Single-content preview — image or autoplay-muted video. The whole
          point of the tooltip is to recognize the creative at a glance, so
          the asset gets the prominent space and the metadata sits as a
          smaller caption below. */}
      {!hasLoop && singleHasPreview && (
        <div style={{
          backgroundColor: '#000',
          maxHeight: 'min(480px, 60vh)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          overflow: 'hidden',
        }}>
          {singleIsVideo ? (
            <video
              src={singleUrl}
              autoPlay
              muted
              loop
              playsInline
              preload="metadata"
              style={{
                width: '100%',
                maxHeight: 'min(480px, 60vh)',
                objectFit: 'contain',
                display: 'block',
              }}
            />
          ) : (
            <img
              src={singleUrl}
              alt=""
              loading="lazy"
              decoding="async"
              onError={(e) => { e.currentTarget.style.display = 'none'; }}
              style={{
                width: '100%',
                maxHeight: 'min(480px, 60vh)',
                objectFit: 'contain',
                display: 'block',
              }}
            />
          )}
        </div>
      )}
      <div style={singleHasPreview ? { padding: '10px 12px' } : undefined}>
        <div style={{ fontWeight: 700, fontSize: '13px', marginBottom: '2px' }}>
          {hasLoop ? '↻ ' : ''}{label}
          {!hasLoop && singleMediaType && (
            <span style={{
              marginLeft: '6px',
              color: 'rgba(255,255,255,0.45)',
              fontSize: '10px',
              fontWeight: 500,
              textTransform: 'uppercase',
              letterSpacing: '0.04em',
            }}>
              {String(singleMediaType).split('/')[0]}
            </span>
          )}
        </div>
        <div style={{ color: 'rgba(255,255,255,0.7)', fontSize: '11px' }}>
          {formatMinutes(startMin)} — {formatMinutes(endMin)} ({durStr})
        </div>
      </div>
      {hasLoop && (
        <>
          <div style={{
            color: 'rgba(255,255,255,0.55)', marginTop: '8px',
            fontSize: '10px', fontWeight: 700, letterSpacing: '0.06em',
            textTransform: 'uppercase',
          }}>
            Loop · {slot.loopItems.length} items · {totalLoopSec}s total
          </div>
          <div style={{
            marginTop: '6px',
            display: 'grid',
            gridTemplateColumns: '96px 1fr auto auto',
            gap: '6px 10px',
            fontSize: '11px',
            alignItems: 'center',
          }}>
            {slot.loopItems.slice(0, LOOP_STRIP_MAX).map((li, idx) => {
              const isNow = idx === activeIdx;
              const t = loopThumbs[idx];
              return (
                <React.Fragment key={idx}>
                  <div style={{
                    width: '96px',
                    height: '54px',
                    borderRadius: '4px',
                    overflow: 'hidden',
                    backgroundColor: 'rgba(255,255,255,0.06)',
                    border: isNow ? '2px solid #fff' : '1px solid rgba(255,255,255,0.12)',
                    boxShadow: isNow ? '0 0 0 2px rgba(255,255,255,0.18)' : 'none',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '14px',
                    color: 'rgba(255,255,255,0.55)',
                  }}>
                    {t?.usableImg ? (
                      <img
                        src={t.url}
                        alt=""
                        loading="lazy"
                        decoding="async"
                        onError={(e) => { e.currentTarget.parentElement.textContent = '⊠'; }}
                        style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                      />
                    ) : (t?.isVideo ? '▶' : '⊠')}
                  </div>
                  <div style={{
                    color: isNow ? '#fff' : 'rgba(255,255,255,0.78)',
                    fontWeight: isNow ? 700 : 500,
                    whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                  }}>
                    {isNow ? '▸ ' : '  '}{resolveName(li.contentId)}
                  </div>
                  <div style={{
                    color: 'rgba(255,255,255,0.5)', fontSize: '10px',
                  }}>
                    {li.mediaType || 'image'}
                  </div>
                  <div style={{
                    color: 'rgba(255,255,255,0.65)', fontVariantNumeric: 'tabular-nums',
                  }}>
                    {li.durationSeconds || 10}s
                  </div>
                </React.Fragment>
              );
            })}
            {loopOverflow > 0 && (
              <div style={{
                gridColumn: '1 / -1',
                marginTop: '2px',
                fontSize: '10px',
                color: 'rgba(255,255,255,0.55)',
                textAlign: 'center',
              }}>
                +{loopOverflow} more {loopOverflow === 1 ? 'item' : 'items'}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

/**
 * Right-side drawer that shows the full list of loop items for a slot.
 * Opened when the operator clicks a dense (>20-item) loop; also works for
 * smaller loops if invoked. Keeps the timeline itself uncluttered by
 * pushing detail into its own surface.
 */
const LoopDrawer = ({ slot, contentMap, nowMs, onClose, onCancelSlot }) => {
  const [cancelBusy, setCancelBusy] = useState(false);
  const [cancelError, setCancelError] = useState(null);
  const [removingItemId, setRemovingItemId] = useState(null);
  const [removeError, setRemoveError] = useState(null);
  useEffect(() => {
    if (!slot) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [slot, onClose]);
  useEffect(() => { setCancelError(null); setCancelBusy(false); setRemoveError(null); }, [slot?.scheduleId]);

  if (!slot) return null;

  const scheduleId = slot.scheduleId || slot.id;
  const handleCancel = async () => {
    if (!scheduleId) { setCancelError('Missing scheduleId on this slot.'); return; }
    if (!window.confirm('Cancel this slot? It will stop playing on this screen immediately.')) return;
    setCancelBusy(true);
    setCancelError(null);
    try {
      await cancelCmsSchedule(scheduleId);
      onCancelSlot?.(scheduleId);
      onClose();
    } catch (err) {
      setCancelError(err?.message || 'Cancel failed.');
    } finally {
      setCancelBusy(false);
    }
  };

  const handleRemoveItem = async (contentId, label) => {
    if (!scheduleId) return;
    if (!window.confirm(`Remove "${label}" from this loop?`)) return;
    setRemovingItemId(contentId);
    setRemoveError(null);
    try {
      await removeLoopItemFromSlot(scheduleId, contentId);
      onCancelSlot?.();
      onClose();
    } catch (err) {
      setRemoveError(err?.message || 'Remove failed.');
    } finally {
      setRemovingItemId(null);
    }
  };

  const items = Array.isArray(slot.loopItems) ? slot.loopItems : [];
  const startMin = timeToMinutes(slot.startTime);
  const endMin = timeToMinutes(slot.endTime);
  const totalSec = items.reduce((a, x) => a + (x.durationSeconds || 10), 0);

  const now = nowMs || Date.now();
  let activeIdx = -1;
  if (totalSec > 0) {
    const todayStart = new Date(now); todayStart.setHours(0, 0, 0, 0);
    const slotStartMs = todayStart.getTime() + startMin * 60 * 1000;
    const nowMin = new Date(now).getHours() * 60 + new Date(now).getMinutes();
    if (nowMin >= startMin && nowMin < endMin) {
      let cur = Math.max(0, Math.floor((now - slotStartMs) / 1000)) % totalSec;
      for (let i = 0; i < items.length; i += 1) {
        const d = items[i].durationSeconds || 10;
        if (cur < d) { activeIdx = i; break; }
        cur -= d;
      }
    }
  }

  const resolve = (cid) => {
    const c = contentMap?.get?.(cid);
    return {
      label: c?.title || c?.name || c?.filename || String(cid || '(unknown)'),
      thumb: c?.thumbnailUrl || null,
      mediaType: c?.mediaType || null,
    };
  };

  return (
    <>
      <div
        onClick={onClose}
        style={{
          position: 'fixed', inset: 0, backgroundColor: 'rgba(17,24,39,0.28)',
          zIndex: 150, backdropFilter: 'blur(1px)',
        }}
      />
      <aside
        role="dialog"
        aria-label="Loop contents"
        style={{
          position: 'fixed', top: 0, right: 0, bottom: 0,
          width: 'min(420px, 92vw)', backgroundColor: '#fff',
          zIndex: 160, boxShadow: '-12px 0 36px rgba(17,24,39,0.18)',
          display: 'flex', flexDirection: 'column',
        }}
      >
        <header style={{
          padding: '14px 18px 12px', borderBottom: '1px solid var(--border-subtle, #ececea)',
          display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '12px',
        }}>
          <div>
            <div style={{
              fontSize: '10px', fontWeight: 700, letterSpacing: '0.08em',
              textTransform: 'uppercase', color: 'var(--text-muted)', marginBottom: '4px',
            }}>
              Loop · {items.length} items · {totalSec}s cycle
            </div>
            <div style={{ fontSize: '15px', fontWeight: 700, color: 'var(--text-strong)' }}>
              {formatMinutes(startMin)} — {formatMinutes(endMin)}
            </div>
            <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginTop: '2px' }}>
              Screen {slot.screenId || '—'}
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <button
              type="button"
              onClick={handleCancel}
              disabled={cancelBusy || !scheduleId}
              title={scheduleId ? 'Stop this slot from playing' : 'This slot has no scheduleId'}
              style={{
                border: '1px solid #fecaca', background: '#fff1f2', color: '#b91c1c',
                cursor: cancelBusy ? 'wait' : 'pointer', fontSize: '11px', fontWeight: 600,
                padding: '4px 10px', borderRadius: '6px', opacity: cancelBusy ? 0.6 : 1,
              }}
            >
              {cancelBusy ? 'Cancelling…' : 'Cancel slot'}
            </button>
            <button
              type="button"
              onClick={onClose}
              title="Close (Esc)"
              style={{
                border: 'none', background: 'transparent', cursor: 'pointer',
                color: 'var(--text-muted)', fontSize: '20px', lineHeight: 1, padding: '2px 4px',
              }}
            >×</button>
          </div>
        </header>
        {cancelError && (
          <div style={{
            padding: '8px 14px', fontSize: '12px', color: '#b91c1c',
            background: '#fef2f2', borderBottom: '1px solid #fecaca',
          }}>
            {cancelError}
          </div>
        )}
        {removeError && (
          <div style={{
            padding: '8px 14px', fontSize: '12px', color: '#b91c1c',
            background: '#fef2f2', borderBottom: '1px solid #fecaca',
          }}>
            {removeError}
          </div>
        )}
        <div style={{ flex: 1, overflowY: 'auto', padding: '8px 10px' }}>
          {items.map((li, idx) => {
            const { label, thumb, mediaType } = resolve(li.contentId);
            const isNow = idx === activeIdx;
            return (
              <div
                key={idx}
                style={{
                  display: 'flex', alignItems: 'center', gap: '10px',
                  padding: '8px 10px', borderRadius: '8px',
                  backgroundColor: isNow ? 'rgba(200,161,71,0.12)' : 'transparent',
                  border: isNow ? '1px solid var(--gold-500, #c8a147)' : '1px solid transparent',
                  marginBottom: '2px',
                }}
              >
                <div style={{
                  width: '18px', fontSize: '10px', color: 'var(--text-muted)',
                  fontVariantNumeric: 'tabular-nums', textAlign: 'right',
                }}>{idx + 1}</div>
                <div style={{
                  width: '36px', height: '36px', borderRadius: '5px', flexShrink: 0,
                  backgroundColor: 'var(--gray-200, #e7e5e4)',
                  backgroundImage: thumb ? `url(${thumb})` : 'none',
                  backgroundSize: 'cover', backgroundPosition: 'center',
                }} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{
                    fontSize: '13px', fontWeight: 600, color: 'var(--text-strong)',
                    whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                  }}>
                    {isNow && <span style={{ marginRight: '4px', color: 'var(--gold-500, #c8a147)' }}>▸</span>}
                    {label}
                  </div>
                  <div style={{ fontSize: '10px', color: 'var(--text-muted)', marginTop: '2px' }}>
                    {(mediaType || 'image').toLowerCase()} · {li.durationSeconds || 10}s
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => handleRemoveItem(li.contentId, label)}
                  disabled={removingItemId === li.contentId}
                  title="Remove from loop"
                  style={{
                    flexShrink: 0, border: 'none', background: 'transparent',
                    cursor: removingItemId === li.contentId ? 'wait' : 'pointer',
                    color: 'var(--text-muted)', fontSize: '16px', lineHeight: 1,
                    padding: '2px 4px', borderRadius: '4px', opacity: removingItemId === li.contentId ? 0.4 : 0.6,
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.opacity = '1'; e.currentTarget.style.color = '#b91c1c'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.opacity = '0.6'; e.currentTarget.style.color = 'var(--text-muted)'; }}
                >
                  ×
                </button>
              </div>
            );
          })}
          {items.length === 0 && (
            <div style={{ padding: '24px 10px', textAlign: 'center', color: 'var(--text-muted)', fontSize: '12px' }}>
              No loop items on this slot.
            </div>
          )}
        </div>
      </aside>
    </>
  );
};

const cardStyle = {
  backgroundColor: 'var(--surface-raised, #fff)',
  border: '1px solid var(--border-default, #e7e5e4)',
  borderRadius: '12px',
  overflow: 'hidden',
};

const loadingStyle = {
  padding: '32px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: 'var(--text-muted)',
  fontSize: '13px',
};

export default ScreenTimeline;
